import cn from "classnames";
import { useTranslation } from "react-i18next";

import { useGetAssistantStats } from "../../apis";
import { AiAssistant, EAiAssistantChannel } from "../../aiAgents.types";
import { AgentAvatar } from "../AgentAvatar";
import { ChannelTag } from "../ChannelTag";

import styles from "./AgentCard.module.scss";

const PLACEHOLDER = "—";

const formatCount = (value: number | undefined) =>
  typeof value === "number" ? value.toLocaleString() : PLACEHOLDER;

const formatRate = (value: number | undefined) =>
  typeof value === "number" ? `${Math.round(value * 100)}%` : PLACEHOLDER;

const CHANNEL_COLOR: Record<EAiAssistantChannel, string> = {
  [EAiAssistantChannel.WhatsApp]: "#733FB7",
  [EAiAssistantChannel.InAppSupport]: "#03BF9D"
};

interface Props {
  assistant: AiAssistant;
  onClick?: () => void;
  onToggleActive?: (next: boolean) => void;
  toggleDisabled?: boolean;
}

const InlineSwitch = ({
  active,
  disabled,
  onChange
}: {
  active: boolean;
  disabled?: boolean;
  onChange?: (next: boolean) => void;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={active}
    disabled={disabled}
    className={cn(styles.switch, { [styles.switchActive]: active })}
    onClick={(e) => {
      e.stopPropagation();
      onChange?.(!active);
    }}
  >
    <span className={styles.switchKnob} />
  </button>
);

const AgentCard = ({
  assistant,
  onClick,
  onToggleActive,
  toggleDisabled
}: Props) => {
  const { t } = useTranslation();
  const { stats } = useGetAssistantStats(assistant.id);

  return (
    <div
      className={styles.card}
      onClick={onClick}
      role={onClick ? "button" : undefined}
    >
      <div className={styles.statusPillWrapper}>
        <span
          className={cn(styles.statusPill, {
            [styles.statusActive]: assistant.isActive,
            [styles.statusInactive]: !assistant.isActive
          })}
        >
          {assistant.isActive ? t("Active") : t("Inactive")}
        </span>
      </div>

      <div className={styles.header}>
        <AgentAvatar
          letter={(assistant.name[0] ?? "A").toUpperCase()}
          color={CHANNEL_COLOR[assistant.channel]}
          size={44}
        />
        <div className={styles.name}>{assistant.name}</div>
      </div>

      <div className={styles.description}>
        {t("Handles orders, returns, and product questions in French.")}
      </div>

      <div className={styles.channels}>
        <ChannelTag channel={assistant.channel} />
      </div>

      <div className={styles.stats}>
        <div className={styles.stat}>
          <div className={styles.statValue}>{formatCount(stats?.convos)}</div>
          <div className={styles.statLabel}>{t("Convos")}</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statValue}>
            {formatRate(stats?.resolvedRate)}
          </div>
          <div className={styles.statLabel}>{t("Resolved")}</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statValue}>
            {formatRate(stats?.handoffRate)}
          </div>
          <div className={styles.statLabel}>{t("Handoff")}</div>
        </div>
      </div>

      <div className={styles.support}>
        <div className={styles.supportText}>
          <div className={styles.supportTitle}>{t("Support agent")}</div>
          <div className={styles.supportSub}>
            {t("Allows agents to answer support inquiries")}
          </div>
        </div>
        <InlineSwitch
          active={assistant.isActive}
          disabled={toggleDisabled}
          onChange={onToggleActive}
        />
      </div>
    </div>
  );
};

export default AgentCard;
